import type { CityState } from '@/core/shared/geolocation/geolocation.persistence';
import type { PlacePrediction } from '@/core/shared/search/types/place-prediction.type';
import { UnitedStatesStates } from './state.utils';

export interface CityStateSuggestion extends CityState {
  key: string;
  title: string;
}

const KNOWN_STATES = new Set(UnitedStatesStates.map((state) => state.abbreviation));
const COUNTRY_SUFFIXES = new Set(['USA', 'UNITED STATES']);
// "MI" or, for a postal-code prediction, "MI 48226".
const STATE_SEGMENT = /^([A-Z]{2})(?:\s+\d{5})?$/;

/**
 * Reads a city/state pair out of a place prediction such as "Detroit, MI, USA",
 * "Detroit, MI 48226, USA" or "North Hollywood, Los Angeles, CA, USA". The segment right before
 * the state is the settlement, so a neighborhood resolves to the city that contains it.
 */
export function parseCityStatePrediction(description: string): CityState | null {
  const segments = description
    .split(',')
    .map((segment) => segment.trim())
    .filter(Boolean);

  if (COUNTRY_SUFFIXES.has((segments[segments.length - 1] ?? '').toUpperCase())) {
    segments.pop();
  }
  if (segments.length < 2) return null;

  const state = STATE_SEGMENT.exec(segments[segments.length - 1])?.[1];
  if (!state || !KNOWN_STATES.has(state)) return null;

  return { city: segments[segments.length - 2], state };
}

/** One suggestion per city/state pair, in prediction order; unparseable predictions are dropped. */
export function toCityStateSuggestions(predictions: PlacePrediction[]): CityStateSuggestion[] {
  const suggestions = new Map<string, CityStateSuggestion>();

  for (const prediction of predictions) {
    const pair = parseCityStatePrediction(prediction.value);
    if (!pair) continue;

    const key = `${pair.city.toLowerCase()}|${pair.state}`;
    if (!suggestions.has(key)) {
      suggestions.set(key, { ...pair, key, title: `${pair.city}, ${pair.state}` });
    }
  }

  return [...suggestions.values()];
}
